import type { ReactNode } from 'react';

import { ADMIN_CARD } from '@/shared/ui-class';
import { cn } from '@/shared/cn';

export type AdminItemCardProps = {
  /** Leading control — typically a Switch or checkbox (top-left). */
  control: ReactNode;
  /** Bold item name. */
  title: ReactNode;
  /** One-line muted description; clamped to two lines. */
  description?: ReactNode;
  /** Trailing pills or status chips (top-right). */
  badges?: ReactNode;
  /** Optional overflow menu (not used on AI Models). */
  menu?: ReactNode;
  /** Dim the card when the item is inactive. */
  muted?: boolean;
  className?: string;
};

/**
 * Compact toggle/badge/title card for small rich catalogs (admin card grids).
 * Renders legibly at ~quarter viewport width (half-width section × 2-up grid).
 */
export function AdminItemCard({
  control,
  title,
  description,
  badges,
  menu,
  muted = false,
  className,
}: AdminItemCardProps) {
  return (
    <div
      className={cn(
        ADMIN_CARD,
        'flex flex-col gap-2 p-3',
        muted && 'opacity-60',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="shrink-0">{control}</div>
        <div className="flex shrink-0 items-center gap-1">
          {badges}
          {menu}
        </div>
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          {title}
        </div>
        {description ? (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted">{description}</p>
        ) : null}
      </div>
    </div>
  );
}
